import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { Metadata, RunView, CompositeKey, UserInfo } from '@memberjunction/core';
import { UUIDsEqual } from '@memberjunction/global';
import { Subject } from 'rxjs';
import { BCSaaSOrganizationCredentialEntity, BCSaaSOrganizationAIConfigurationEntity } from '@bluecypress/bcsaas-entities';
import { scopeUserToOrganization } from '@bluecypress/bcsaas-core';
import { BCAIConfigurationEngine } from '@bluecypress/bcsaas-ai-configuration';
import { BCAuthorizationEngine } from '@bluecypress/bcsaas-authorizations';
import { mjBizAppsCommonOrganizationEntity } from '@mj-biz-apps/common-entities';
import { BCSaaSAIConfigurationEntity } from '@bluecypress/bcsaas-entities';
import { MJCredentialEntity, MJCredentialTypeEntity } from '@memberjunction/core-entities';
import { IzzyMetadataEngine } from 'mj_generatedentities';
import { IAdminPanel, AdminPanelStatus } from '../admin-panel.interface';
import { DeleteOrgCredential, DisplayCredentialName } from '../../utils/org-credentials';

/**
 * Display wrapper for AI configurations
 */
interface AIConfigDisplay {
  config: BCSaaSAIConfigurationEntity;
  isSelected: boolean;
}

interface AIConfigState {
  selectedOrganizationId?: string;
}

/**
 * Known AI vendor driver classes and their display info
 */
const KNOWN_DRIVERS: { [key: string]: { vendorName: string; icon: string } } = {
  'OpenAILLM': { vendorName: 'OpenAI', icon: 'fa-robot' },
  'AnthropicLLM': { vendorName: 'Anthropic', icon: 'fa-brain' },
  'GeminiLLM': { vendorName: 'Google', icon: 'fa-google' },
  'GroqLLM': { vendorName: 'Groq', icon: 'fa-bolt' },
  'CerebrasLLM': { vendorName: 'Cerebras', icon: 'fa-microchip' },
  'MistralLLM': { vendorName: 'Mistral', icon: 'fa-wind' }
};

/**
 * AI Configuration Management Component
 *
 * Provides interface for:
 * - Selecting which AI configuration an organization uses
 * - Managing AI vendor API key credentials
 *
 * This component works with the AIConfigurationEngine on the backend
 * to resolve and validate AI configurations per organization.
 */
@Component({
  standalone: false,
  selector: 'izzy-ai-configuration',
  templateUrl: './ai-configuration.component.html',
  styleUrls: ['./ai-configuration.component.css']
})
export class AIConfigurationComponent implements OnInit, OnDestroy, OnChanges, IAdminPanel {

  @Input() initialState: AIConfigState | null = null;
  @Input() organizationId: string | undefined;
  @Output() stateChange = new EventEmitter<AIConfigState>();
  @Output() statusChange = new EventEmitter<AdminPanelStatus>();
  @Output() openRecord = new EventEmitter<{ EntityName: string; RecordPKey: CompositeKey }>();

  constructor() {}

  // Data
  protected organizations: mjBizAppsCommonOrganizationEntity[] = [];
  protected aiConfigurations: AIConfigDisplay[] = [];
  protected aiVendorCredentials: MJCredentialEntity[] = [];
  protected aiVendorCredentialType: MJCredentialTypeEntity | null = null;

  // Selection State
  protected selectedOrganization: mjBizAppsCommonOrganizationEntity | null = null;
  private orgAIConfigLinks: BCSaaSOrganizationAIConfigurationEntity[] = [];

  // UI State
  protected isLoading: boolean = true;
  protected isSaving: boolean = false;
  protected errorMessage: string = '';
  protected successMessage: string = '';

  /** Credential name without BCSaaS's ` [organizationID]` suffix (template helper). */
  protected readonly displayName = DisplayCredentialName;

  private destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    await this.loadData();

    // Apply initial state
    if (this.organizationId) {
      const org = this.organizations.find(o => o.ID === this.organizationId);
      if (org) {
        this.selectOrganization(org);
      }
    } else if (this.initialState?.selectedOrganizationId) {
      const org = this.organizations.find(o => o.ID === this.initialState!.selectedOrganizationId);
      if (org) {
        this.selectOrganization(org);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // React to organizationId input changes
    if (changes['organizationId'] && !changes['organizationId'].firstChange) {
      const newOrgId = changes['organizationId'].currentValue;
      if (newOrgId && this.organizations.length > 0) {
        const org = this.organizations.find(o => o.ID === newOrgId);
        if (org && org.ID !== this.selectedOrganization?.ID) {
          this.selectOrganization(org);
        }
      }
    }
  }

  protected async loadData(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      // Ensure IzzyMetadataEngine is loaded
      await IzzyMetadataEngine.Instance.Config(false);

      this.organizations = IzzyMetadataEngine.Instance.GetCurrentUserOrganizations();

      // Load AI configurations
      await this.loadAIConfigurations();

      // Load AI Vendor API Key credential type
      await this.loadCredentialType();

      // Build driver class options from known drivers

      if (!this.selectedOrganization && this.organizations.length > 0) {
        this.selectOrganization(this.organizations[0]);
      }
    } catch (error) {
      console.error('Error loading AI configuration data:', error);
      this.errorMessage = 'Failed to load data. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  private async loadAIConfigurations(): Promise<void> {
    const rv = new RunView();
    const result = await rv.RunView<BCSaaSAIConfigurationEntity>({
      EntityName: 'BC: AI Configurations',
      ExtraFilter: "Status = 'Active' AND (AvailableUntil IS NULL OR AvailableUntil > GETUTCDATE())",
      OrderBy: 'Sequence ASC',
      ResultType: 'entity_object'
    });

    if (result.Success) {
      this.aiConfigurations = result.Results.map(config => ({
        config,
        isSelected: false
      }));
    }
  }

  private async loadCredentialType(): Promise<void> {
    const rv = new RunView();
    const result = await rv.RunView<MJCredentialTypeEntity>({
      EntityName: 'MJ: Credential Types',
      ExtraFilter: "Name = 'AI Vendor API Key'",
      ResultType: 'entity_object'
    });

    if (result.Success && result.Results.length > 0) {
      this.aiVendorCredentialType = result.Results[0];
    }
  }

  private async loadCredentials(): Promise<void> {
    if (!this.selectedOrganization || !this.aiVendorCredentialType) {
      this.aiVendorCredentials = [];
      return;
    }

    try {
      // Load organization credentials via bridge table
      const rv = new RunView();
      const orgCredResult = await rv.RunView<BCSaaSOrganizationCredentialEntity>({
        EntityName: 'BC: Organization Credentials',
        ExtraFilter: `OrganizationID = '${this.selectedOrganization.ID}' AND Status = 'Active'`,
        ResultType: 'entity_object'
      });

      if (orgCredResult.Success && orgCredResult.Results.length > 0) {
        const credentialIds = orgCredResult.Results.map(oc => oc.CredentialID);
        const idList = credentialIds.map(id => `'${id}'`).join(',');

        const credResult = await rv.RunView<MJCredentialEntity>({
          EntityName: 'MJ: Credentials',
          ExtraFilter: `ID IN (${idList}) AND CredentialTypeID = '${this.aiVendorCredentialType.ID}' AND IsActive = 1`,
          OrderBy: 'Name ASC',
          ResultType: 'entity_object'
        });

        this.aiVendorCredentials = credResult.Success ? credResult.Results : [];
      } else {
        this.aiVendorCredentials = [];
      }

      // Update configuration selection states
      this.updateConfigurationStates();

      // Emit status after credentials are loaded
      this.emitPanelStatus();
    } catch (error) {
      console.error('Error loading AI credentials:', error);
    }
  }

  private getRootOrganizationId(organizationId: string): string {
    const ancestors = IzzyMetadataEngine.Instance.GetOrganizationAncestors(organizationId);
    if (ancestors.length > 0) {
      return ancestors[ancestors.length - 1].ID;
    }
    return organizationId;
  }

  private async loadOrgAIConfigLinks(): Promise<void> {
    if (!this.selectedOrganization) {
      this.orgAIConfigLinks = [];
      return;
    }
    const rv = new RunView();
    const result = await rv.RunView<BCSaaSOrganizationAIConfigurationEntity>({
      EntityName: 'BC: Organization AI Configurations',
      ExtraFilter: `OrganizationID = '${this.selectedOrganization.ID}'`,
      ResultType: 'entity_object'
    });
    this.orgAIConfigLinks = result.Success ? result.Results : [];
  }

  private updateConfigurationStates(): void {
    if (!this.selectedOrganization) return;

    const activeConfigIds = new Set(
      this.orgAIConfigLinks.filter(l => l.IsActive).map(l => l.AIConfigurationID.toLowerCase())
    );

    for (const display of this.aiConfigurations) {
      display.isSelected = activeConfigIds.has(display.config.ID.toLowerCase());
    }
  }

  // Organization Selection

  async selectOrganization(org: mjBizAppsCommonOrganizationEntity): Promise<void> {
    this.selectedOrganization = org;
    await this.loadOrgAIConfigLinks();
    await this.loadCredentials();
    this.emitStateChange();
  }

  onOrganizationChange(orgId: string): void {
    const org = this.organizations.find(o => o.ID === orgId);
    if (org) {
      this.selectOrganization(org);
    }
  }

  // Configuration Selection

  async selectConfiguration(display: AIConfigDisplay): Promise<void> {
    if (!this.selectedOrganization || this.isSaving) return;

    this.isSaving = true;
    this.errorMessage = '';

    try {
      // Go through BCSaaS's own engine, the path the BCSaaS panel and the other BCSaaS apps use.
      // It demotes the current default before promoting the new one (required by the
      // UQ_OrgAIConfiguration_SingleDefault filtered unique index, BCSaaS 1.9+), and applies the
      // plan-level and vendor-credential rules. The engine acts on the user's tenant
      // organization, so the user is scoped to the organization chosen in this panel.
      const scopedUser = this.scopedUserForSelectedOrganization();
      await this.healInactiveDefaults();
      const engine = await this.loadEngines(scopedUser);
      await engine.ActivateConfiguration(display.config.ID, scopedUser, { setAsDefault: true });

      // This panel models one active configuration per organization: retire the others. The
      // new one is already the default, so none of these deactivations promotes a replacement.
      await engine.Config(true, scopedUser);
      for (const link of engine.GetActivatedConfigurations(this.selectedOrganization.ID)) {
        if (!UUIDsEqual(link.AIConfigurationID, display.config.ID)) {
          await engine.DeactivateConfiguration(link.AIConfigurationID, scopedUser);
        }
      }
      await engine.Config(true, scopedUser);

      // Reload bridge records and refresh state
      await this.loadOrgAIConfigLinks();
      this.updateConfigurationStates();
      this.showSuccess(`AI Configuration set to "${display.config.Name}"`);
      this.emitPanelStatus();

    } catch (error) {
      console.error('Error selecting configuration:', error);
      this.errorMessage = `Failed to update AI configuration: ${error instanceof Error ? error.message : String(error)}`;
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * A copy of the current user whose tenant context points at the organization selected in this
   * panel, so the BCSaaS engine acts on that organization (BCSaaS's documented route for tooling
   * that acts on another organization's behalf). The session user is never mutated.
   */
  private scopedUserForSelectedOrganization(): UserInfo {
    const user = new Metadata().CurrentUser;
    if (!user || !this.selectedOrganization) {
      throw new Error('No signed-in user or selected organization');
    }
    return scopeUserToOrganization(user, this.selectedOrganization.ID);
  }

  /**
   * The engine checks BCSaaS.Settings.Write through the client authorization engine, which nothing
   * else in Izzy loads; load it (cached after the first call) so a non-system organization admin
   * is judged by their role rather than refused for an empty authorization list.
   */
  private async loadEngines(scopedUser: UserInfo): Promise<BCAIConfigurationEngine> {
    await BCAuthorizationEngine.Instance.Config(false, scopedUser);
    const engine = BCAIConfigurationEngine.Instance;
    await engine.Config(true, scopedUser);
    return engine;
  }

  /**
   * Repairs rows the pre-engine version of this panel left behind: a deactivated link that still
   * carries IsDefault = 1. The engine only looks at active rows when it demotes the current
   * default, so such a row would make the next promotion violate UQ_OrgAIConfiguration_SingleDefault.
   * One save per row; a failure throws before any promotion is attempted.
   */
  private async healInactiveDefaults(): Promise<void> {
    for (const link of this.orgAIConfigLinks.filter(l => !l.IsActive && l.IsDefault)) {
      link.IsDefault = false;
      if (!await link.Save()) {
        throw new Error(`Failed to clear a stale default on an inactive AI configuration link: ${link.LatestResult?.CompleteMessage ?? 'save failed'}`);
      }
    }
  }

  async clearConfiguration(): Promise<void> {
    if (!this.selectedOrganization || this.isSaving) return;

    this.isSaving = true;
    this.errorMessage = '';

    try {
      // Deactivate every active configuration through the engine. The default goes last so the
      // engine does not promote a replacement on the way to "none active".
      const scopedUser = this.scopedUserForSelectedOrganization();
      await this.healInactiveDefaults();
      const engine = await this.loadEngines(scopedUser);
      const active = engine.GetActivatedConfigurations(this.selectedOrganization.ID)
        .sort((a, b) => Number(a.IsDefault) - Number(b.IsDefault));
      for (const link of active) {
        await engine.DeactivateConfiguration(link.AIConfigurationID, scopedUser);
      }
      await engine.Config(true, scopedUser);

      // Reload bridge records and refresh state
      await this.loadOrgAIConfigLinks();
      this.updateConfigurationStates();
      this.showSuccess('AI Configuration cleared - will inherit from parent organization');
      this.emitPanelStatus();

    } catch (error) {
      console.error('Error clearing configuration:', error);
      this.errorMessage = `Failed to clear AI configuration: ${error instanceof Error ? error.message : String(error)}`;
    } finally {
      this.isSaving = false;
    }
  }

  // Credential Management (keys are added in the Credentials section)

  async deleteCredential(credential: MJCredentialEntity): Promise<void> {
    if (this.isSaving) return;

    if (!confirm(`Are you sure you want to delete "${DisplayCredentialName(credential.Name)}"? Any AI operations using this credential will fail.`)) {
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';

    try {
      if (!this.selectedOrganization) {
        throw new Error('No organization selected');
      }
      // Same path as the Credentials section: BCSaaS's engine removes the credential-use junction
      // rows and the organization link, then the credential when no other organization uses it.
      await DeleteOrgCredential(this.selectedOrganization.ID, credential.ID);

      await this.loadCredentials();
      this.showSuccess('Credential deleted');

    } catch (error) {
      console.error('Error deleting credential:', error);
      this.errorMessage = `Failed to delete credential: ${error instanceof Error ? error.message : String(error)}`;
    } finally {
      this.isSaving = false;
    }
  }

  // Helpers

  getVendorIcon(driverClass: string): string {
    return KNOWN_DRIVERS[driverClass]?.icon || 'fa-microchip';
  }

  getVendorName(driverClass: string): string {
    return KNOWN_DRIVERS[driverClass]?.vendorName || driverClass;
  }

  // State Management

  private emitStateChange(): void {
    this.stateChange.emit({
      selectedOrganizationId: this.selectedOrganization?.ID
    });
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => { this.successMessage = ''; }, 3000);
  }

  // ========== Admin Panel Status ==========

  /**
   * Calculate and emit the panel status.
   * Called after data changes that could affect status.
   */
  private emitPanelStatus(): void {
    const status = this.calculatePanelStatus();
    this.statusChange.emit(status);
  }

  /**
   * Calculate the panel status based on:
   * 1. Whether an AI configuration is selected for the org
   * 2. Whether the org has AI vendor credentials
   *
   * Note: Ideally we'd check if credentials match the required drivers for the
   * selected config, but that requires server-side logic. For now, we check
   * if there are ANY AI vendor credentials when a config is selected.
   */
  private calculatePanelStatus(): AdminPanelStatus {
    if (!this.selectedOrganization) {
      return { status: 'none', tooltip: '' };
    }

    const hasConfig = this.orgAIConfigLinks.length > 0;
    const hasCredentials = this.aiVendorCredentials.length > 0;

    if (!hasConfig) {
      return {
        status: 'warning',
        tooltip: 'No AI configuration selected'
      };
    }

    if (!hasCredentials) {
      return {
        status: 'error',
        tooltip: 'AI configuration selected but no credentials configured'
      };
    }

    // Get the selected config name for the tooltip
    const selectedConfig = this.aiConfigurations.find(c => c.isSelected);
    const configName = selectedConfig?.config.Name || 'AI';

    return {
      status: 'good',
      tooltip: `${configName} configured with ${this.aiVendorCredentials.length} credential(s)`
    };
  }
}

export function LoadAIConfigurationComponent(): void {}
